import { useRef } from "react";
import vehicleTools from "../../../../helpers/vehicleTools";
import useTVA from "../../../../hooks/useInit/useTVA";
import { num } from "./formatters";

const STATUS_FALLBACK = { 1: "Brouillon", 2: "Validé", 3: "Finalisé" };

export const BENEFIT_KEYS = [
  { key: "Pneus", label: "Pneus" },
  { key: "Assistance", label: "Assistance" },
  { key: "Remise_en_etat", label: "Remise en état" },
];

// Fonction pure : (devis, constantes, résolveur d'investissement) -> stats.
// Conçue sans accès au store ni à l'API pour rester testable isolément.
export const computeDossierStats = (
  quotations,
  constantData,
  getInvestAmtHT
) => {
  const marginRateMin = num(constantData.MARGIN_RATE_MIN);
  const statusNames = { ...STATUS_FALLBACK };
  (constantData.QUOTATION_STATUS || []).forEach((s) => {
    statusNames[s.id] = s.name;
  });

  let vehicleCount = 0;
  let engagementTTC = 0;
  let monthlyRentHT = 0;
  let investTotalHT = 0;
  let contractsRemaining = 0;
  let vpCount = 0;
  let vuCount = 0;
  let marginTotalOverDuration = 0;
  let marginWeightSum = 0;
  let marginInvestSum = 0;

  const statusCounts = {};
  const issues = [];
  const issueIndexes = new Set();
  const rents = [];
  const marginRows = [];
  const cellMap = {};
  const brandMap = {};
  const benefitCounts = {};
  BENEFIT_KEYS.forEach((b) => (benefitCounts[b.key] = 0));

  quotations.forEach((q, index) => {
    const qty = num(q.quantity) || 1;
    const dur = num(q.dur);
    const km = num(q.km);
    const rentHT = num(q.totalRentHT);
    const rentTTC = num(q.totalRentTTC);
    const invest = getInvestAmtHT(q);
    const reference = q.reference || "Devis " + (index + 1);

    vehicleCount += qty;
    monthlyRentHT += rentHT * qty;
    engagementTTC += rentTTC * dur * qty;
    investTotalHT += invest * qty;

    const statusId = q.status?.id || 1;
    statusCounts[statusId] = (statusCounts[statusId] || 0) + 1;
    const generated = (q.contracts || []).length;
    contractsRemaining += Math.max(0, qty - generated);

    // Mêmes règles que missingData() de useQuotationDetail
    if (num(q.repurchaseValueFinal) <= 0) {
      issues.push({ index, reference, label: "valeur de rachat manquante" });
      issueIndexes.add(index);
    }
    if (num(q.maintenanceValueFinal) < 0 || q.maintenanceValueFinal + "" === "") {
      issues.push({ index, reference, label: "maintenance manquante" });
      issueIndexes.add(index);
    }
    // parseInt (et non num) pour rester strictement aligné sur missingData()
    if (parseInt(q.totalRentHT) <= 0) {
      issues.push({ index, reference, label: "loyer erroné" });
      issueIndexes.add(index);
    }

    if (rentHT > 0) {
      rents.push({ index, reference, rentHT });
    }

    const rate = num(q.marginTTCRate);
    marginTotalOverDuration += num(q.marginTTC) * dur * qty;
    if (invest > 0) {
      marginWeightSum += rate * invest * qty;
      marginInvestSum += invest * qty;
    }
    marginRows.push({
      index,
      reference,
      rate,
      isLow: rate < marginRateMin + 1,
    });

    if (dur > 0 && km > 0) {
      const key = dur + "|" + km;
      if (!cellMap[key]) {
        cellMap[key] = { dur, km, count: 0, rentSum: 0 };
      }
      cellMap[key].count += 1;
      cellMap[key].rentSum += rentHT;
    }

    const brandName = q.vehicle?.brand?.name || "Autre";
    brandMap[brandName] = (brandMap[brandName] || 0) + qty;
    const rawType = q.vehicle?.type;
    const typeId = rawType && rawType.id !== undefined ? rawType.id : rawType;
    if (parseInt(typeId) === 1) {
      vpCount += qty;
    } else {
      vuCount += qty;
    }

    BENEFIT_KEYS.forEach((b) => {
      if ((q.benefits || []).some((bn) => bn.name === b.key)) {
        benefitCounts[b.key] += 1;
      }
    });
  });

  rents.sort((a, b) => a.rentHT - b.rentHT);
  let rentStats = null;
  if (rents.length > 0) {
    const values = rents.map((r) => r.rentHT);
    const min = values[0];
    const max = values[values.length - 1];
    const mid = Math.floor(values.length / 2);
    const median =
      values.length % 2 === 1
        ? values[mid]
        : (values[mid - 1] + values[mid]) / 2;
    rentStats = { min, max, median, spread: max - min };
  }

  marginRows.sort((a, b) => a.rate - b.rate);

  const cells = Object.values(cellMap).map((c) => ({
    ...c,
    avgRentHT: c.count > 0 ? c.rentSum / c.count : 0,
  }));
  const durations = [...new Set(cells.map((c) => c.dur))].sort(
    (a, b) => a - b
  );
  const kms = [...new Set(cells.map((c) => c.km))].sort((a, b) => a - b);
  const maxCellCount = cells.reduce((m, c) => Math.max(m, c.count), 0);

  return {
    count: quotations.length,
    vehicleCount,
    engagementTTC,
    monthlyRentHT,
    investTotalHT,
    statuses: [1, 2, 3]
      .map((id) => ({
        id,
        name: statusNames[id],
        count: statusCounts[id] || 0,
      }))
      .filter((s) => s.count > 0),
    contractsRemaining,
    issues,
    readyCount: quotations.length - issueIndexes.size,
    rents,
    rentStats,
    margins: {
      totalOverDuration: marginTotalOverDuration,
      weightedRate: marginInvestSum > 0 ? marginWeightSum / marginInvestSum : 0,
      rows: marginRows,
    },
    durKm: { durations, kms, cells, maxCellCount },
    brands: Object.entries(brandMap)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty),
    vpCount,
    vuCount,
    benefits: BENEFIT_KEYS.map((b) => ({
      key: b.key,
      label: b.label,
      covered: benefitCounts[b.key],
      total: quotations.length,
    })),
  };
};

const useDossierStats = (quotations, constantData) => {
  const { getTVAByCatalog } = useTVA();
  const tvaCache = useRef({});

  const getInvestAmtHT = (q) => {
    const direct = num(q.investAmtHT);
    if (direct > 0) return direct;
    const catalogUuid = q.vehicle?.catalog?.uuid;
    if (!catalogUuid) return 0;
    // cache par uuid : getTVAByCatalog toaste en cas d'erreur, ne pas
    // l'appeler à chaque rendu
    if (!(catalogUuid in tvaCache.current)) {
      tvaCache.current[catalogUuid] = getTVAByCatalog(catalogUuid);
    }
    const { TVA, TVA_INVERT } = tvaCache.current[catalogUuid];
    if (TVA === -1) return 0;
    return num(vehicleTools.calcInvestAmtHT(q, TVA, TVA_INVERT));
  };

  // Pas de useMemo : le reducer mute quotations en place (même référence),
  // un memo keyé dessus resterait obsolète. Le calcul est négligeable.
  return computeDossierStats(quotations || [], constantData || {}, getInvestAmtHT);
};

export default useDossierStats;
